<template>
  <div>
    <div class="KPIs-container">
      <div class="KPIs-item">
        <div>
          <v-progress-circular v-if="questionsPending"
                               indeterminate
                               color="primary"
                               class="KPIs-numberLoader"
                               size="40"
          />
          <div v-else class="KPIs-number">
            {{ counterAllQuestions }}
          </div>
        </div>
        <div
          v-if="questionsLimit !== null && remainingQuestions !== null"
          :class="limitBadgeClass"
          :data-state="limitBadgeState"
          class="KPIs-limit-badge KPIs-text"
          data-locator="qa-limit-badge"
        >
          <span data-locator="qa-limit-remaining">
            {{  $t('project.question-answer.QuestionAnswerListHeader.remainingQuestionsCount', { count: remainingQuestions ?? 0 }) }}
          </span>
          <v-menu location="bottom start" open-on-hover>
            <template #activator="{ props }">
              <span v-bind="props" class="d-inline-flex" data-locator="qa-limit-info-icon">
                <app-material-icon name="info" size="xs" :color="limitIconColor" />
              </span>
            </template>
            <v-card class="KPIs-info-card pa-3" max-width="295" elevation="2" data-locator="qa-limit-info-card">
              <template v-if="isQuestionsLimitReached">
                <p
                  :class="['KPIs-info-card__title mb-1', { 'KPIs-info-card__title--guest': !isCurrentUserPm }]"
                  data-locator="qa-limit-info-card-title"
                >
                  {{ $t('project.question-answer.QuestionAnswerListHeader.limitReachedTooltipTitle') }}
                </p>
                <p v-if="isCurrentUserPm" class="KPIs-info-card__subtitle" data-locator="qa-limit-info-card-subtitle">
                  {{ $t('project.question-answer.QuestionAnswerListHeader.limitReachedTooltipSubtitle') }}
                </p>
              </template>
              <p v-else class="KPIs-info-card__subtitle" data-locator="qa-limit-info-card-subtitle">
                {{ isCurrentUserPm ? $t('project.question-answer.QuestionAnswerListHeader.limitInfoTooltip') : $t('project.question-answer.QuestionAnswerListHeader.limitInfoTooltipGuest') }}
              </p>
              <p v-if="questionsLimitResetDate" class="KPIs-info-card__subtitle">
                {{ $t('project.question-answer.QuestionAnswerListHeader.limitInfoTooltipFrequency', { date: questionsLimitResetDateFormatted, time: DEFAULT_LIMIT_TIME }) }}
              </p>
            </v-card>
          </v-menu>
        </div>
        <div v-else class="KPIs-text" data-locator="qa-questions-kpi-text">
          {{ $t('project.question-answer.QuestionAnswerListHeader.totalQuestionsCount') }}
        </div>
      </div>
      <div class="KPIs-item">
        <div>
          <v-progress-circular v-if="questionsPending"
                               indeterminate
                               color="primary"
                               class="KPIs-numberLoader"
                               size="40"
          />
          <div v-else class="KPIs-number">
            {{ counterAwaitingQuestions }}
          </div>
        </div>
        <div class="KPIs-text">
          <QuestionAnswerStatusIcon :status="QA_STATUS.TO_ANSWER" :useTooltip="false" />
          <span>{{ $t('project.question-answer.QuestionAnswerListHeader.pendingQuestionsCount') }}</span>
        </div>
      </div>
      <div class="KPIs-item">
        <div>
          <v-progress-circular v-if="questionsPending"
                               indeterminate
                               color="primary"
                               class="KPIs-numberLoader"
                               size="40"
          />
          <div v-else class="KPIs-number">
            {{ counterAwaitingAnswers }}
          </div>
        </div>
        <div class="KPIs-text">
          <QuestionAnswerStatusIcon :status="QA_STATUS.TO_VALIDATE" :useTooltip="false" />
          <span>{{ $t('project.question-answer.QuestionAnswerListHeader.pendingAnswersCount') }}</span>
        </div>
      </div>
      <div class="KPIs-item">
        <div>
          <v-progress-circular v-if="questionsPending"
                               indeterminate
                               color="primary"
                               class="KPIs-numberLoader"
                               size="40"
          />
          <div v-else class="KPIs-number">
            {{ counterValidatedQuestions }}
          </div>
        </div>
        <div class="KPIs-text">
          <QuestionAnswerStatusIcon :status="QA_STATUS.VALIDATED" :useTooltip="false" />
          <span>{{ $t('project.question-answer.QuestionAnswerListHeader.answeredQuestionsCount') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapState } from 'vuex'

import { QA_STATUS } from '@/common/utils/qa-status'
import QuestionAnswerStatusIcon from '@/project/question-answer/QuestionAnswerStatusIcon.vue'

const LIMIT_WARNING_THRESHOLD = 0.8
const LIMIT_DANGER_THRESHOLD = 0.95
const DEFAULT_LIMIT_TIME = '00:00'

export default {
  name: 'QuestionAnswerListHeader',
  components: { QuestionAnswerStatusIcon },
  data () {
    return {
      QA_STATUS,
      DEFAULT_LIMIT_TIME,
    }
  },
  computed: {
    ...mapState('questionAnswer', ['questionsPending']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('questionAnswer', [
      'counterAllQuestions',
      'counterAwaitingQuestions',
      'counterValidatedQuestions',
      'counterAwaitingAnswers',
      'questionsLimit',
      'questionsLimitResetDate',
      'remainingQuestions',
      'isQuestionsLimitReached',
    ]),
    limitUsageRatio () {
      if (!this.questionsLimit) return 0
      return (this.questionsLimit - (this.remainingQuestions ?? 0)) / this.questionsLimit
    },
    isDangerWarning () {
      return this.isQuestionsLimitReached || this.limitUsageRatio >= LIMIT_DANGER_THRESHOLD
    },
    isYellowWarning () {
      return !this.isDangerWarning && this.limitUsageRatio >= LIMIT_WARNING_THRESHOLD
    },
    limitBadgeClass () {
      if (this.isDangerWarning) return 'KPIs-limit-badge--danger'
      if (this.isYellowWarning) return 'KPIs-limit-badge--warning'
      return 'KPIs-limit-badge--default'
    },
    limitBadgeState () {
      if (this.isDangerWarning) return 'danger'
      if (this.isYellowWarning) return 'warning'
      return 'default'
    },
    limitIconColor () {
      if (this.isDangerWarning) return 'rgb(var(--v-theme-icon-danger-bold))'
      if (this.isYellowWarning) return 'rgb(var(--v-theme-icon-warning-bold))'
      return ''
    },
    questionsLimitResetDateFormatted () {
      return this.questionsLimitResetDate ? dayjs(this.questionsLimitResetDate).utc().format('YYYY/MM/DD') : null
    },
  },
}
</script>

<style scoped lang="scss">
.KPIs {
  &-container {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  &-item {
    flex: 1;
    padding: 24px;
    background-color: rgb(var(--v-theme-surface-primary));
    border-radius: 8px;
    border: 1px solid rgb(var(--v-theme-border-primary));
    text-align: center;
  }

  &-number {
    color: rgb(var(--v-theme-text-secondary));
    font-size: 60px;
    line-height: 60px;
    margin-bottom: 22px;
  }

  &-numberLoader {
    margin-bottom: 32px;
    margin-top: 10px;
  }

  &-text {
    font-size: 16px;
    font-weight: 500;
    color: rgb(var(--v-theme-text-tertiary));
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
  }

  &-limit-badge {
    display: inline-flex;
    align-items: center;
    column-gap: 4px;
    // The 0 vertical padding is done to align with the rest of the KPI texts
    padding: 0px 12px;
    border-radius: 100px;

    &--default {
      background-color: transparent;
      color: rgb(var(--v-theme-text-tertiary)) !important;
      padding: 0;
    }

    &--warning {
      background-color: rgb(var(--v-theme-surface-warning-subtle));
      color: rgb(var(--v-theme-surface-warning-bold)) !important;
    }

    &--danger {
      background-color: rgb(var(--v-theme-surface-danger));
      color: rgb(var(--v-theme-text-danger)) !important;
    }
  }

  &-info-card {
    &__title,
    &__subtitle {
      font-size: 12px;
      line-height: 16px;
      color: rgb(var(--v-theme-text-tertiary));
    }

    &__title {
      font-weight: 500;

      &--guest {
        font-weight: 400;
      }
    }

    &__subtitle {
      font-weight: 400;
    }
  }
}
</style>
